// O questionário de abertura de empresa: as etapas que o cliente do aluno
// responde, e os rótulos com que as respostas são lidas de volta.
//
// Puro, com prova própria em `app/tests/abertura-de-empresa.mjs`.
//
// ---------------------------------------------------------------------------
// DOIS FORMATOS CONVIVEM NO DADO, E OS DOIS PRECISAM SER LEGÍVEIS
// ---------------------------------------------------------------------------
// Medido nas 60 respostas da origem em 04/09/2026: **42 estão no formato antigo**
// (seções `identificacao`, `documentacao`, `contato_socios`, `endereco_residencial`,
// `participacao`, `complementares`, `expectativas`, `formalizacao`) e **18 no
// `schema_version: 2`** (`responsavel`, `socios`, `dados_empresa`, …).
//
// O formulário de hoje grava só o formato 2. Os rótulos do formato antigo ficam
// porque 42 clientes reais responderam nele, e uma resposta que a tela não sabe
// ler vira uma chave crua na frente do contador — ou some.
//
// ---------------------------------------------------------------------------
// O JSONB NÃO PRESERVA A ORDEM DAS CHAVES
// ---------------------------------------------------------------------------
// Por isso existe `ORDEM_DAS_SECOES`. Sem ela, a mesma resposta apareceria em
// ordens diferentes a cada leitura, e o contador que confere duas seguidas
// acharia que o dado mudou.

// ---------------------------------------------------------------------------
// As etapas do formulário
// ---------------------------------------------------------------------------

export type TipoDeCampo = "texto" | "texto-longo" | "escolha" | "multipla" | "moeda" | "telefone" | "cep" | "cnpj";

export interface Campo {
  id: string;
  rotulo: string;
  tipo: TipoDeCampo;
  opcoes?: string[];
  /** Só aparece quando o campo `quando.campo` estiver com o valor `quando.valor`. */
  quando?: { campo: string; valor: string };
  dica?: string;
}

export interface Etapa {
  chave: string;
  titulo: string;
  descricao?: string;
  campos: Campo[];
}

const FORMAS_DE_ATUACAO = [
  "Estabelecimento fixo",
  "Internet",
  "Em local fixo fora de loja",
  "Correio",
  "Porta a porta, postos móveis ou por ambulantes",
  "Televendas",
  "Máquinas automáticas",
  "Atividade desenvolvida fora do estabelecimento",
];

const ESTADO_CIVIL = ["Solteiro(a)", "Casado(a)", "Divorciado(a)", "Viúvo(a)", "União estável"];

/** A ficha de uma pessoa — o responsável e cada sócio respondem a mesma. */
export function camposDaPessoa(ehResponsavel: boolean): Campo[] {
  return [
    { id: "nome", rotulo: "Nome completo", tipo: "texto" },
    { id: "email", rotulo: "E-mail", tipo: "texto" },
    { id: "telefone", rotulo: "Telefone", tipo: "telefone" },
    { id: "estado_civil", rotulo: "Estado civil", tipo: "escolha", opcoes: ESTADO_CIVIL },
    {
      id: "regime_bens",
      rotulo: "Regime de bens",
      tipo: "texto",
      // Só quem é casado tem regime de bens. União estável também pode ter
      // contrato de convivência, e por isso ela entra — mas a condição é por
      // VALOR, e a tela cuida de mostrar nos dois casos.
      quando: { campo: "estado_civil", valor: "Casado(a)" },
    },
    { id: "profissao", rotulo: "Profissão", tipo: "texto" },
    {
      id: "registro_orgao_classe",
      rotulo: "Tem registro em órgão de classe? (CRM, CRO, OAB…)",
      tipo: "escolha",
      opcoes: ["Sim", "Não"],
    },
    {
      id: "qual_orgao",
      rotulo: "Qual órgão?",
      tipo: "texto",
      quando: { campo: "registro_orgao_classe", valor: "Sim" },
    },
    {
      id: "certificado_ecpf",
      rotulo: "Já tem certificado digital e-CPF?",
      tipo: "escolha",
      opcoes: ["Sim", "Não"],
    },
    { id: "cep", rotulo: "CEP da residência", tipo: "cep" },
    { id: "rua", rotulo: "Rua", tipo: "texto" },
    { id: "numero", rotulo: "Número", tipo: "texto" },
    { id: "complemento", rotulo: "Complemento", tipo: "texto" },
    { id: "bairro", rotulo: "Bairro", tipo: "texto" },
    { id: "cidade", rotulo: "Cidade", tipo: "texto" },
    { id: "estado", rotulo: "Estado", tipo: "texto" },
    {
      id: "funcao",
      rotulo: "Qual será a função na empresa?",
      tipo: "escolha",
      opcoes: ["Titular", "Sócio", "Administrador", "Sócio-administrador"],
    },
    {
      id: "responsavel_administracao",
      rotulo: "Será responsável pela administração?",
      tipo: "escolha",
      opcoes: ["Sim", "Não"],
    },
    ...(ehResponsavel
      ? ([
          { id: "tera_socio", rotulo: "A empresa terá sócios?", tipo: "escolha", opcoes: ["Sim", "Não"] },
          {
            id: "quantidade_socios",
            rotulo: "Quantos sócios além de você?",
            tipo: "escolha",
            opcoes: ["1", "2", "3", "4", "5", "6", "7", "8", "9"],
            quando: { campo: "tera_socio", valor: "Sim" },
          },
        ] as Campo[])
      : []),
    { id: "empresa_aberta", rotulo: "Já tem outra empresa aberta?", tipo: "escolha", opcoes: ["Sim", "Não"] },
    {
      id: "cnpj_existente",
      rotulo: "Qual o CNPJ?",
      tipo: "cnpj",
      quando: { campo: "empresa_aberta", valor: "Sim" },
    },
    {
      id: "debitos_publicos",
      rotulo: "Tem débitos com órgãos públicos?",
      tipo: "escolha",
      opcoes: ["Sim", "Não"],
    },
  ];
}

/** As etapas fixas, depois da ficha do responsável e dos sócios. */
export const ETAPAS: Etapa[] = [
  {
    chave: "dados_empresa",
    titulo: "Dados da empresa",
    descricao: "O nome empresarial é o que vai no contrato social; o fantasia é o nome da porta.",
    campos: [
      { id: "nome_opcao_1", rotulo: "Nome empresarial — 1ª opção", tipo: "texto" },
      { id: "nome_opcao_2", rotulo: "Nome empresarial — 2ª opção", tipo: "texto" },
      { id: "nome_opcao_3", rotulo: "Nome empresarial — 3ª opção", tipo: "texto" },
      { id: "nome_fantasia", rotulo: "Nome fantasia", tipo: "texto" },
      {
        id: "tipo_empresa",
        rotulo: "Tipo de empresa pretendida",
        tipo: "escolha",
        opcoes: [
          "MEI",
          "Empresário Individual",
          "Sociedade LTDA (mais de 1 sócio)",
          "Sociedade LTDA Unipessoal (apenas 1 sócio)",
          "Outro",
        ],
      },
      {
        id: "tipo_empresa_outro",
        rotulo: "Qual tipo?",
        tipo: "texto",
        quando: { campo: "tipo_empresa", valor: "Outro" },
      },
      {
        id: "porte",
        rotulo: "Porte estimado",
        tipo: "escolha",
        opcoes: [
          "ME — faturamento anual de até R$ 360.000,00",
          "EPP — faturamento anual de até R$ 4.800.000,00",
        ],
      },
    ],
  },
  {
    chave: "atividade",
    titulo: "Atividades",
    campos: [
      {
        id: "descricao_atividade",
        rotulo: "Descreva o que a empresa vai fazer",
        tipo: "texto-longo",
        dica: "Quanto mais detalhe, mais fácil escolher o CNAE certo.",
      },
      { id: "cnae", rotulo: "CNAE principal, se souber", tipo: "texto" },
      {
        id: "atividades_secundarias",
        rotulo: "Haverá atividades secundárias?",
        tipo: "escolha",
        opcoes: ["Sim", "Não"],
      },
      {
        id: "quais_secundarias",
        rotulo: "Quais? (CNAEs, se souber)",
        tipo: "texto-longo",
        quando: { campo: "atividades_secundarias", valor: "Sim" },
      },
      {
        id: "exercera_no_local",
        rotulo: "A atividade será exercida no endereço da empresa?",
        tipo: "escolha",
        opcoes: ["Sim", "Não"],
      },
      {
        id: "forma_atuacao",
        rotulo: "Como a empresa vai atuar? (pode marcar mais de uma)",
        tipo: "multipla",
        opcoes: FORMAS_DE_ATUACAO,
      },
    ],
  },
  {
    chave: "endereco_empreendimento",
    titulo: "Endereço da empresa",
    campos: [
      { id: "cep", rotulo: "CEP", tipo: "cep" },
      { id: "rua", rotulo: "Rua", tipo: "texto" },
      { id: "numero", rotulo: "Número", tipo: "texto" },
      { id: "complemento", rotulo: "Complemento", tipo: "texto" },
      { id: "bairro", rotulo: "Bairro", tipo: "texto" },
      { id: "cidade", rotulo: "Cidade", tipo: "texto" },
      { id: "estado", rotulo: "Estado", tipo: "texto" },
      {
        id: "situacao_imovel",
        rotulo: "Situação do imóvel",
        tipo: "escolha",
        opcoes: ["Próprio", "Alugado", "Cedido"],
      },
      { id: "numero_iptu", rotulo: "Inscrição do IPTU", tipo: "texto" },
      { id: "area_total", rotulo: "Área total (m²)", tipo: "texto" },
      { id: "area_utilizada", rotulo: "Área utilizada (m²)", tipo: "texto" },
    ],
  },
  {
    chave: "capital_social",
    titulo: "Capital social",
    campos: [
      { id: "capital_social", rotulo: "Valor do capital social", tipo: "moeda" },
      {
        id: "forma_integralizacao",
        rotulo: "Como será integralizado?",
        tipo: "escolha",
        opcoes: ["Dinheiro", "Bens móveis", "Bens imóveis"],
      },
      {
        id: "prazo_integralizacao",
        rotulo: "Prazo de integralização",
        tipo: "escolha",
        opcoes: ["Imediato", "Parcelado"],
      },
      {
        id: "prazo_parcelado",
        rotulo: "Em quanto tempo?",
        tipo: "texto",
        quando: { campo: "prazo_integralizacao", valor: "Parcelado" },
      },
    ],
  },
  {
    chave: "contato_empresa",
    titulo: "Contato da empresa",
    campos: [
      { id: "telefone", rotulo: "Telefone", tipo: "telefone" },
      { id: "email", rotulo: "E-mail", tipo: "texto" },
    ],
  },
  {
    chave: "estrutura",
    titulo: "Estrutura",
    campos: [
      { id: "possui_estoque", rotulo: "Vai manter estoque?", tipo: "escolha", opcoes: ["Sim", "Não"] },
      { id: "qtd_funcionarios", rotulo: "Quantos funcionários no começo?", tipo: "texto" },
      { id: "emissao_nf", rotulo: "Vai emitir nota fiscal?", tipo: "escolha", opcoes: ["Sim", "Não"] },
    ],
  },
  {
    chave: "faturamento",
    titulo: "Faturamento e enquadramento",
    campos: [
      { id: "faturamento_mensal", rotulo: "Faturamento mensal estimado", tipo: "moeda" },
      { id: "faturamento_anual", rotulo: "Faturamento anual estimado", tipo: "moeda" },
      {
        id: "simples_nacional",
        rotulo: "Pretende optar pelo Simples Nacional?",
        tipo: "escolha",
        opcoes: ["Sim", "Não", "Não sei"],
      },
      {
        id: "participacao_outra",
        rotulo: "Algum sócio participa de outra empresa?",
        tipo: "escolha",
        opcoes: ["Sim", "Não"],
      },
      {
        id: "ultrapassa_48m",
        rotulo: "O faturamento somado ultrapassa R$ 4,8 milhões por ano?",
        tipo: "escolha",
        opcoes: ["Sim", "Não", "Não sei"],
      },
      {
        id: "dentro_sublimite",
        rotulo: "O faturamento fica abaixo de R$ 3,6 milhões por ano?",
        tipo: "escolha",
        opcoes: ["Sim", "Não", "Não sei"],
      },
    ],
  },
  {
    chave: "reforma",
    titulo: "Reforma Tributária",
    descricao: "A partir de 2027, CBS e IBS mudam a forma de recolher. Estas respostas ajudam o contador a orientar.",
    campos: [
      {
        id: "analise_hibrido",
        rotulo: "Quer análise do regime híbrido (CBS/IBS fora do Simples)?",
        tipo: "escolha",
        opcoes: ["Sim", "Não", "Não sei o que é"],
      },
      {
        id: "ciente_prazos",
        rotulo: "Está ciente dos prazos de opção?",
        tipo: "escolha",
        opcoes: ["Sim", "Não"],
      },
      { id: "simulacao_tributaria", rotulo: "Quer uma simulação tributária?", tipo: "escolha", opcoes: ["Sim", "Não"] },
      {
        id: "contabilidade_mensal",
        rotulo: "Vai contratar contabilidade mensal?",
        tipo: "escolha",
        opcoes: ["Sim", "Não — só a abertura"],
      },
    ],
  },
];

/** O texto das declarações finais. */
export const DECLARACOES = [
  "As informações que prestei são verdadeiras.",
  "Estou ciente das obrigações fiscais e contábeis da empresa.",
  "Autorizo o contador a realizar os procedimentos necessários à abertura.",
];

export const MAX_SOCIOS = 9;

// ---------------------------------------------------------------------------
// A leitura das respostas
// ---------------------------------------------------------------------------

export const ROTULO_DA_SECAO: Record<string, string> = {
  // Formato 2 — o que o hub grava.
  responsavel: "Responsável",
  socios: "Sócios",
  dados_empresa: "Dados da empresa",
  atividade: "Atividades",
  endereco_empreendimento: "Endereço da empresa",
  capital_social: "Capital social",
  contato_empresa: "Contato da empresa",
  estrutura: "Estrutura",
  faturamento: "Faturamento e enquadramento",
  reforma: "Reforma Tributária",
  declaracoes: "Declarações",
  documentos: "Documentos",
  // Formato antigo — 42 das 60 respostas da origem estão nele.
  identificacao: "Identificação do responsável",
  documentacao: "Documentação do responsável",
  contato_socios: "Contato",
  endereco_residencial: "Endereço residencial",
  participacao: "Participação na empresa",
  complementares: "Informações complementares",
  expectativas: "Expectativas do cliente",
  formalizacao: "Formalização",
};

/** A ordem em que as seções aparecem. O JSONB não preserva a ordem das chaves. */
export const ORDEM_DAS_SECOES = [
  "responsavel",
  "socios",
  "identificacao",
  "documentacao",
  "contato_socios",
  "endereco_residencial",
  "participacao",
  "dados_empresa",
  "atividade",
  "endereco_empreendimento",
  "capital_social",
  "contato_empresa",
  "estrutura",
  "faturamento",
  "reforma",
  "complementares",
  "documentos",
  "declaracoes",
  "expectativas",
  "formalizacao",
];

const ROTULOS_DA_PESSOA: Record<string, string> = Object.fromEntries(
  camposDaPessoa(true).map((c) => [c.id, c.rotulo]),
);

const ROTULOS_POR_SECAO: Record<string, Record<string, string>> = {
  responsavel: ROTULOS_DA_PESSOA,
  ...Object.fromEntries(
    ETAPAS.map((e) => [e.chave, Object.fromEntries(e.campos.map((c) => [c.id, c.rotulo]))]),
  ),
  declaracoes: { concordo: "Concordou com as declarações" },
  // Formato antigo. Os rótulos vêm do que a tela da origem mostrava — sem eles,
  // 42 respostas apareceriam com as chaves cruas na frente do contador.
  identificacao: {
    nome_completo: "Nome completo",
    cpf: "CPF",
    rg: "RG",
    data_nascimento: "Data de nascimento",
    nacionalidade: "Nacionalidade",
    naturalidade: "Naturalidade",
    nome_mae: "Nome da mãe",
    nome_pai: "Nome do pai",
    estado_civil: "Estado civil",
    regime_bens: "Regime de bens",
    profissao: "Profissão",
    registro_orgao_classe: "Registro em órgão de classe",
    qual_orgao: "Qual órgão",
  },
  documentacao: {
    documento_identificacao: "Documento de identificação",
    cpf_separado: "CPF (documento à parte)",
    certidao_civil: "Certidão civil",
    titulo_eleitor: "Título de eleitor",
    recibo_irpf: "Recibo do último IRPF",
    certificado_ecpf: "Certificado digital e-CPF",
    senha_govbr: "Senha gov.br",
  },
  contato_socios: {
    telefone: "Telefone (WhatsApp)",
    email_principal: "E-mail principal",
    email_secundario: "E-mail secundário",
  },
  endereco_residencial: {
    cep: "CEP",
    rua: "Rua",
    numero: "Número",
    complemento: "Complemento",
    bairro: "Bairro",
    cidade: "Cidade",
    estado: "Estado",
  },
  participacao: {
    funcao: "Função na empresa",
    responsavel_administracao: "Responsável pela administração",
    tera_socio: "Terá sócio",
    quantidade_socios: "Quantidade de sócios",
    funcao_socios: "Função dos sócios",
    percentual_participacao: "Percentual de participação",
  },
  complementares: {
    empresa_aberta: "Já tem empresa aberta",
    cnpj_existente: "CNPJ existente",
    debitos_publicos: "Débitos com órgãos públicos",
  },
  expectativas: {
    objetivo: "Objetivo com a empresa",
    pretende_crescer: "Pretende crescer",
    apoio_necessario: "Apoio necessário",
  },
  formalizacao: {
    ciente_honorarios: "Ciente dos honorários",
    data_inicio: "Data prevista para o início",
  },
  documentos: {
    comprovante_endereco: "Comprovante de endereço",
    iptu: "IPTU do imóvel",
    contrato_locacao: "Contrato de locação",
    avcb: "AVCB",
  },
};

export type CampoLido = { chave: string; rotulo: string; valor: unknown };
export type SecaoLida = { chave: string; campos: CampoLido[] };

/**
 * Transforma o JSONB da resposta em seções ordenadas, com rótulo em cada campo.
 *
 * Os sócios viram uma seção por pessoa (`socios[0]` → "Sócio 2"), porque é assim
 * que o contador lê: uma ficha por gente, não um array.
 */
export function secoesDe(respostas: Record<string, unknown>): SecaoLida[] {
  const posicao = (chave: string) => {
    const i = ORDEM_DAS_SECOES.indexOf(chave);
    // Chave desconhecida vai para o FIM, e não some: uma versão futura do
    // formulário grava algo que esta tela não conhece, e o contador tem de ver.
    return i === -1 ? ORDEM_DAS_SECOES.length : i;
  };

  const saida: SecaoLida[] = [];
  const chaves = Object.keys(respostas)
    .filter((k) => k !== "schema_version")
    .sort((a, b) => posicao(a) - posicao(b));

  for (const chave of chaves) {
    const valor = respostas[chave];

    if (chave === "socios" && Array.isArray(valor)) {
      valor.forEach((socio, i) => {
        if (!socio || typeof socio !== "object") return;
        saida.push({
          // "Sócio 2" porque o responsável é o 1. Numerar a partir de 1 aqui
          // faria dois "Sócio 1" na mesma tela.
          chave: `Sócio ${i + 2}`,
          campos: camposDe(socio as Record<string, unknown>, ROTULOS_DA_PESSOA),
        });
      });
      continue;
    }

    if (!valor || typeof valor !== "object") {
      saida.push({ chave, campos: [{ chave, rotulo: ROTULO_DA_SECAO[chave] ?? chave, valor }] });
      continue;
    }

    const campos = camposDe(valor as Record<string, unknown>, ROTULOS_POR_SECAO[chave] ?? {});
    if (campos.length > 0) saida.push({ chave, campos });
  }

  return saida;
}

function camposDe(secao: Record<string, unknown>, rotulos: Record<string, string>): CampoLido[] {
  return Object.entries(secao)
    // Campo vazio não vira linha: o cliente pulou, e uma lista de traços esconde
    // o que ele de fato respondeu.
    .filter(([, v]) => v !== "" && v !== null && v !== undefined && !(Array.isArray(v) && v.length === 0))
    .map(([chave, valor]) => ({ chave, rotulo: rotulos[chave] ?? chave, valor }));
}

/** O valor de uma resposta como texto legível. */
export function textoDaResposta(valor: unknown): string {
  if (Array.isArray(valor)) return valor.join(", ");
  if (typeof valor === "boolean") return valor ? "Sim" : "Não";
  if (valor === null || valor === undefined || valor === "") return "—";
  return String(valor);
}

/**
 * Há alguma coisa preenchida nesta resposta?
 *
 * `schema_version` não conta: ele é metadado, e um formulário só com ele é um
 * formulário vazio. O banco recusa esse caso, e a tela avisa antes para o
 * cliente não perder a ida ao servidor.
 */
export function temConteudo(respostas: Record<string, unknown>): boolean {
  const cheio = (v: unknown): boolean => {
    if (Array.isArray(v)) return v.some(cheio);
    if (v && typeof v === "object") return Object.values(v).some(cheio);
    return v !== "" && v !== null && v !== undefined && v !== false;
  };
  return Object.entries(respostas).some(([k, v]) => k !== "schema_version" && cheio(v));
}
